import '../scrollytelling.css'
import '../v3/office.css'
import '../v7/baukasten.css'
import './woche.css'

import {
  ArrowDownIcon,
  CalendarDaysIcon,
  CheckIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { AIVY_MEETING_URL } from '../../../helpers'
import { SceneProvider, useFrame, useScrollStory } from '../engine'
import { anim } from '../motion'
import { BeatsProvider } from '../v3/beats'
import {
  AnalysisScreen,
  AtsPanel,
  FairPanel,
  GamePanel,
  InterviewCard,
  MagicPanel,
  OptionCards,
  PdfPanel,
  ProfilePanel,
  RulePanel,
  SupportPanel,
  TalentPhone
} from '../v3/panels'
import { BEATS, DAYS, TOTAL_VH, atB, inB } from './beats'
import { STEPS, de, hoursOf } from './copy'
import { CrossMock, LinkMock, StageShapes, TalentList, TeamMock } from './mocks'

/**
 * Version 9 „Deine Woche“, für Recruiter:innen und HR-Teams. Erst ein
 * kurzer Rechner mit den eigenen Zahlen, dann eine Recruiting-Woche mit
 * Aivy von Montag bis Freitag. Links immer genau ein Gedanke, rechts genau
 * ein Stück Produkt.
 */

const BEATS_VALUE = { inB, atB }
const BY_ID = Object.fromEntries(BEATS.map((b) => [b.id, b]))
const numbers = PropTypes.shape({
  jobs: PropTypes.number.isRequired,
  apps: PropTypes.number.isRequired,
  minutes: PropTypes.number.isRequired
}).isRequired

export default function ScrollytellingV9() {
  const [nums, setNums] = useState({ jobs: 20, apps: 80, minutes: 5 })
  const story = useScrollStory(1, { mode: 'pin' })

  const start = () => {
    const el = story.chapterRefs.current[0]
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY,
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  return (
    <div className='sty-root v9-root'>
      <Calculator nums={nums} onChange={setNums} onStart={start} />
      <div ref={story.storyRef}>
        <section
          ref={(el) => (story.chapterRefs.current[0] = el)}
          className='v9-section'
          style={{ height: `${TOTAL_VH}vh` }}
          aria-label='Eine Recruiting-Woche mit Aivy'
        >
          <div
            ref={(el) => (story.sceneRefs.current[0] = el)}
            className='v9-sticky'
          >
            <SceneProvider index={0} subscribe={story.subscribe}>
              <BeatsProvider value={BEATS_VALUE}>
                <Week nums={nums} />
              </BeatsProvider>
            </SceneProvider>
          </div>
        </section>
      </div>
    </div>
  )
}

// ---------- Rechner vorab ----------

const SLIDERS = [
  { key: 'jobs', label: 'Offene Stellen pro Jahr', min: 1, max: 150, step: 1 },
  {
    key: 'apps',
    label: 'Bewerbungen pro Stelle',
    min: 10,
    max: 500,
    step: 10
  },
  {
    key: 'minutes',
    label: 'Minuten pro Lebenslauf',
    min: 1,
    max: 20,
    step: 1
  }
]

function Calculator({ nums, onChange, onStart }) {
  const hours = hoursOf(nums)
  return (
    <section className='v9-calc' aria-labelledby='v9-calc-title'>
      <div className='v9-calc__inner'>
        <div className='v9-calc__intro'>
          <div className='off-eyebrow'>Für Recruiter:innen und HR-Teams</div>
          <h1 id='v9-calc-title' className='v9-calc__title'>
            Wie viel Zeit steckt in deinen Lebensläufen?
          </h1>
          <p className='v9-calc__lead'>
            Schieb die Regler auf deine Zahlen. Danach zeigen wir dir eine
            Recruiting-Woche mit Aivy, von der neuen Stelle bis zur Zusage.
          </p>
        </div>

        <div className='v9-calc__box'>
          {SLIDERS.map((s) => (
            <label key={s.key} className='v9-slider'>
              <span className='v9-slider__head'>
                {s.label}
                <b>{de(nums[s.key])}</b>
              </span>
              <input
                type='range'
                min={s.min}
                max={s.max}
                step={s.step}
                value={nums[s.key]}
                onChange={(e) =>
                  onChange({ ...nums, [s.key]: Number(e.target.value) })
                }
              />
            </label>
          ))}

          <div className='v9-result' aria-live='polite'>
            <span className='v9-result__num'>{de(hours)}</span>
            <span className='v9-result__unit'>Stunden im Jahr</span>
            <p>
              Das sind rund {de(hours / 8)} Arbeitstage, nur fürs Lesen von
              Lebensläufen. Bei {de(nums.jobs * nums.apps)} Bewerbungen im Jahr.
            </p>
          </div>

          <button type='button' className='off-cta v9-start' onClick={onStart}>
            Zeig mir die Woche mit Aivy <ArrowDownIcon width={16} height={16} />
          </button>
        </div>
      </div>
    </section>
  )
}

Calculator.propTypes = {
  nums: numbers,
  onChange: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired
}

// ---------- Die Woche ----------

const dayOf = (id) => DAYS.find((d) => d.id === id)

function Week({ nums }) {
  const [active, setActive] = useState(0)

  useFrame((l) => {
    const i = BEATS.findIndex((b) => l >= b.at[0] && l < b.at[1])
    setActive(i === -1 ? BEATS.length - 1 : i)
  })

  const day = BEATS[active].day
  const dayIndex = DAYS.findIndex((d) => d.id === day)

  return (
    <div className='v9-stage-wrap'>
      <header className='v9-week'>
        <CalendarDaysIcon width={18} height={18} />
        {DAYS.map((d, i) => (
          <span
            key={d.id}
            className={clsx(
              'v9-week__day',
              i === dayIndex && 'is-active',
              i < dayIndex && 'is-done'
            )}
          >
            {i < dayIndex && <CheckIcon width={12} height={12} />}
            <b>{d.short}</b>
            <span>{d.topic}</span>
          </span>
        ))}
      </header>

      <div className='v9-text'>
        <Intro />
        {Object.keys(STEPS).map((id) => (
          <Step key={id} id={id} nums={nums} />
        ))}
        <Finale />
      </div>

      <div className='v9-stage'>
        <StageShapes />
        <WeekCard />
        <Slot id='hr'>
          <OptionCards />
        </Slot>
        <Slot id='analyse'>
          <AnalysisStack />
        </Slot>
        <ProfilePanel />
        <LinkMock />
        <GamePanel />
        <Slot id='talente'>
          <TalentPhone />
        </Slot>
        <TalentList apps={nums.apps} />
        <Slot id='magic'>
          <MagicPanel />
        </Slot>
        <RulePanel />
        <PdfPanel />
        <Slot id='gespraech' className='v9-slot--interview'>
          <InterviewCard />
        </Slot>
        <TeamMock />
        <CrossMock />
        <FairPanel />
        <AtsPanel />
        <SupportPanel />
        <Result nums={nums} />
      </div>
    </div>
  )
}

Week.propTypes = { nums: numbers }

/** Ein Text-Block links, sichtbar über die Dauer seiner Station. */
function TextBlock({ id, keep, children }) {
  const [s, e] = BY_ID[id].at
  const len = e - s
  const d = dayOf(BY_ID[id].day)
  return (
    <div
      className='v9-step'
      style={anim({
        at: id === 'intro' ? [-1, 0] : [s + len * 0.02, s + len * 0.12],
        out: keep ? undefined : [e - len * 0.1, e],
        y: 30,
        oy: -30
      })}
    >
      <div className='v9-step__day'>
        {d.label} · {d.topic}
      </div>
      {children}
    </div>
  )
}

TextBlock.propTypes = {
  id: PropTypes.string.isRequired,
  keep: PropTypes.bool,
  children: PropTypes.node
}

function Step({ id, nums }) {
  const { title, points } = STEPS[id]
  return (
    <TextBlock id={id}>
      <h2 className='v9-step__title'>{title}</h2>
      <ul className='v9-step__points'>
        {points(nums).map((p) => (
          <li key={p}>
            <CheckIcon width={16} height={16} />
            {p}
          </li>
        ))}
      </ul>
    </TextBlock>
  )
}

Step.propTypes = { id: PropTypes.string.isRequired, nums: numbers }

function Intro() {
  return (
    <TextBlock id='intro'>
      <h2 className='v9-step__title'>Eine Woche Recruiting mit Aivy.</h2>
      <ul className='v9-step__points'>
        <li>
          <CheckIcon width={16} height={16} />
          Eine neue Stelle im Customer Success, von der ersten Idee bis zur
          Zusage.
        </li>
        <li>
          <CheckIcon width={16} height={16} />
          Scroll einfach weiter, Tag für Tag.
        </li>
      </ul>
    </TextBlock>
  )
}

const RECAP = [
  'Profil mit dem Fachbereich abgestimmt',
  'Talente über alle Kanäle eingeladen',
  'Vorauswahl nach Passung, nachvollziehbar',
  'Interviews mit passenden Fragen geführt',
  'Faire Absagen, alles im ATS'
]

function Finale() {
  return (
    <TextBlock id='finale' keep>
      <h2 className='v9-step__title'>Deine Woche mit Aivy.</h2>
      <ul className='v9-step__points v9-recap'>
        {RECAP.map((r, i) => (
          <li
            key={r}
            style={anim({
              at: inB('finale', 0.12 + i * 0.06, 0.22 + i * 0.06),
              x: -12,
              y: 0
            })}
          >
            <span className='v9-recap__day'>{DAYS[i].short}</span>
            {r}
          </li>
        ))}
      </ul>
      <div className='v9-ctas' style={anim({ at: inB('finale', 0.5, 0.6) })}>
        <Link to='/career/new' className='off-cta'>
          Stelle anlegen
        </Link>
        <a
          href={AIVY_MEETING_URL}
          target='_blank'
          rel='noreferrer'
          className='off-cta off-cta--ghost'
        >
          Gespräch vereinbaren
        </a>
      </div>
    </TextBlock>
  )
}

// ---------- Bühne rechts ----------

function Slot({ id, className, children }) {
  const [s, e] = BY_ID[id].at
  const len = e - s
  return (
    <div
      className={clsx('v9-slot', className)}
      style={anim({
        at: [s + len * 0.02, s + len * 0.1],
        out: [e - len * 0.08, e],
        y: 30,
        s: 0.96
      })}
    >
      {children}
    </div>
  )
}

Slot.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node
}

const EXPERTS = [
  {
    who: 'Teamleitung Customer Success',
    q: 'Wie wichtig ist Emotionswahrnehmung?'
  },
  { who: 'Senior Customer Success Manager', q: 'Wie wichtig ist Teamarbeit?' },
  { who: 'Vertrieb', q: 'Wie wichtig ist Gewissenhaftigkeit?' }
]

function AnalysisStack() {
  return (
    <div className='v9-analysis'>
      {EXPERTS.map((e, i) => (
        <div key={e.who} className='v9-analysis__item'>
          <span className='v9-analysis__who'>{e.who}</span>
          <AnalysisScreen delay={i * 0.04} question={e.q} />
        </div>
      ))}
    </div>
  )
}

function WeekCard() {
  return (
    <Slot id='intro'>
      <div className='v9-card v9-weekcard'>
        <div className='v9-card__eyebrow'>Deine Woche</div>
        <div className='v9-card__title'>Customer Success Manager:in</div>
        {DAYS.map((d) => (
          <div key={d.id} className='v9-weekcard__row'>
            <b>{d.label}</b>
            <span>{d.topic}</span>
          </div>
        ))}
      </div>
    </Slot>
  )
}

function Result({ nums }) {
  const hours = hoursOf(nums)
  return (
    <div
      className='v9-slot'
      style={anim({ at: inB('finale', 0.04, 0.16), y: 30, s: 0.96 })}
    >
      <div className='v9-card v9-final'>
        <div className='v9-card__eyebrow'>Deine Zahlen</div>
        <div className='v9-final__row'>
          <span className='v9-final__num'>{de(nums.jobs * nums.apps)}</span>
          <span>Bewerbungen im Jahr, sortiert nach Passung</span>
        </div>
        <div className='v9-final__row'>
          <span className='v9-final__num v9-final__num--old'>
            {de(hours)}&nbsp;h
          </span>
          <span>
            stecken bisher allein im Lesen von Lebensläufen. Mit Aivy startest
            du stattdessen mit einer sortierten Liste.
          </span>
        </div>
        <div className='v9-final__row'>
          <span className='v9-final__num v9-final__num--pink'>100&nbsp;%</span>
          <span>Stärkenprofil für alle Bewerbenden</span>
        </div>
      </div>
    </div>
  )
}

Result.propTypes = { nums: numbers }
